import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const Navbar: React.FC = () => {
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ar' : 'en');
  };

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50 transition-all duration-300 border-b-2 border-light-green-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Left side - Logo and Navigation */}
          <div className="flex items-center space-x-8 rtl:space-x-reverse">
            {/* Logo */}
            <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
              <div className="flex-shrink-0">
                <img 
                  src="/assets/logo.svg" 
                  alt="TourBook Logo" 
                  className="h-12 w-auto"
                />
              </div>
            </Link>

            {/* Navigation Links */}
            <div className="hidden md:flex space-x-6 rtl:space-x-reverse">
              <Link
                to="/"
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                  isActive('/')
                    ? 'text-light-green-600 bg-light-green-50 shadow-sm'
                    : 'text-gray-700 hover:text-light-green-600 hover:bg-gray-50'
                }`}
              >
                {t('nav.home')}
              </Link>
              <Link
                to="/booking"
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                  isActive('/booking')
                    ? 'text-saudi-green-600 bg-saudi-green-50 shadow-sm'
                    : 'text-gray-700 hover:text-saudi-green-600 hover:bg-gray-50'
                }`}
              >
                {t('nav.booking')}
              </Link>
              <Link
                to="/about"
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                  isActive('/about')
                    ? 'text-saudi-green-600 bg-saudi-green-50 shadow-sm'
                    : 'text-gray-700 hover:text-saudi-green-600 hover:bg-gray-50'
                }`}
              >
                {t('nav.about')}
              </Link>
              <Link
                to="/blog"
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                  isActive('/blog')
                    ? 'text-saudi-green-600 bg-saudi-green-50 shadow-sm'
                    : 'text-gray-700 hover:text-saudi-green-600 hover:bg-gray-50'
                }`}
              >
                {t('nav.blog')}
              </Link>
            </div>
          </div>

          {/* Right side - Language Toggle and Profile Icon */}
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg text-gray-700 hover:text-light-green-600 hover:bg-gray-50 transition-all duration-300 rtl:space-x-reverse"
              aria-label="Toggle language"
            >
              <Globe size={20} />
              <span className="font-semibold text-sm">{language === 'en' ? 'AR' : 'EN'}</span>
            </button>
            <button
              className="p-2 rounded-full text-gray-700 hover:text-light-green-600 hover:bg-gray-100 transition-all duration-300"
              aria-label="Profile"
            >
              <User size={24} />
            </button>
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-all"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white animate-slideDown">
          <div className="px-4 pt-4 pb-6 space-y-2">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                isActive('/')
                  ? 'text-light-green-600 bg-light-green-50'
                  : 'text-gray-700 hover:text-light-green-600 hover:bg-gray-50'
              }`}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/booking"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                isActive('/booking')
                  ? 'text-light-green-600 bg-light-green-50'
                  : 'text-gray-700 hover:text-light-green-600 hover:bg-gray-50'
              }`}
            >
              {t('nav.booking')}
            </Link>
            <Link
              to="/about"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                isActive('/about')
                  ? 'text-light-green-600 bg-light-green-50'
                  : 'text-gray-700 hover:text-light-green-600 hover:bg-gray-50'
              }`}
            >
              {t('nav.about')}
            </Link>
            <Link
              to="/blog"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                isActive('/blog')
                  ? 'text-light-green-600 bg-light-green-50'
                  : 'text-gray-700 hover:text-light-green-600 hover:bg-gray-50'
              }`}
            >
              {t('nav.blog')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
